'use client';

import { useMemo } from 'react';
import type { WebhookAuthConfig } from '@capture/shared';
import { isForbiddenWebhookUrl } from '@capture/shared';
// Faithful extraction (transform-webhook T5) of the WU-3.2 T4 connection
// sub-form from RuleEditorDrawer — this component is now the SINGLE home for
// url/auth/timeout fields, shared by both the validation-webhook rule panel
// (RuleEditorDrawer) and the new webhook DERIVATION behavior (WebhookBody).
// The CSS is imported directly here (rather than relying on the host to have
// already loaded it) so the fields render correctly in either context.
// P3c (2026-07-08) — relocated from rules/RuleEditorDrawer.css (the legacy Rules
// tab + editor were retired) into this behaviors-local stylesheet, which is now
// the sole home for the webhook connection field styles.
import './webhook-connection-fields.css';

/** The connection-only slice of `WebhookBehaviorConfig` / `WebhookValidateConfig`
 *  — both shapes agree on url/auth/timeoutMs, so this component works for
 *  either caller without needing to know about failMode/message/requestBody. */
export interface WebhookConnectionValue {
  url: string;
  auth?: WebhookAuthConfig;
  timeoutMs?: number;
}

export interface WebhookConnectionFieldsProps {
  value: WebhookConnectionValue;
  /** Receives the FULL next value (not a patch) — mirrors the value/onChange
   *  contract used across the form-config panels (e.g. MappingPanel rows).
   *  A caller that needs to physically delete a key (e.g. remove `auth`
   *  entirely rather than set it to `undefined`) should special-case
   *  `next.auth === undefined` the way RuleEditorDrawer does. */
  onChange: (next: WebhookConnectionValue) => void;
}

const WEBHOOK_AUTH_DEFAULTS: Record<WebhookAuthConfig['type'], WebhookAuthConfig> = {
  none: { type: 'none' },
  bearer: { type: 'bearer', token: '' },
  basic: { type: 'basic', username: '', password: '' },
  api_key: { type: 'api_key', headerName: '', key: '' },
};

export function WebhookConnectionFields({ value, onChange }: WebhookConnectionFieldsProps) {
  const urlValue = value.url ?? '';
  // Client-side mirror of `isForbiddenWebhookUrl` — the SAME predicate the
  // publish gate / batch gate use server-side (WU-3.2 T1 / transform-webhook
  // T2) — so the operator sees the SSRF/protocol problem inline.
  const urlProblem = useMemo(
    () => (urlValue ? isForbiddenWebhookUrl(urlValue) : null),
    [urlValue],
  );

  const authType: WebhookAuthConfig['type'] = value.auth?.type ?? 'none';
  const authBearer = authType === 'bearer' ? (value.auth as Extract<WebhookAuthConfig, { type: 'bearer' }>) : undefined;
  const authBasic = authType === 'basic' ? (value.auth as Extract<WebhookAuthConfig, { type: 'basic' }>) : undefined;
  const authApiKey = authType === 'api_key' ? (value.auth as Extract<WebhookAuthConfig, { type: 'api_key' }>) : undefined;

  function handleAuthTypeChange(type: WebhookAuthConfig['type']) {
    if (type === 'none') {
      onChange({ ...value, auth: undefined });
      return;
    }
    onChange({ ...value, auth: WEBHOOK_AUTH_DEFAULTS[type] });
  }

  /** Merges a partial patch into the current auth sub-object, preserving
   *  sibling fields (e.g. keeps `password` when only `username` changes). */
  function updateAuth(patch: Partial<WebhookAuthConfig>) {
    onChange({ ...value, auth: { ...value.auth, ...patch } as WebhookAuthConfig });
  }

  return (
    <>
      <div className="rule-editor-drawer__field">
        <label className="rule-editor-drawer__label">Webhook URL</label>
        <input
          value={urlValue}
          onChange={(e) => onChange({ ...value, url: e.target.value })}
          placeholder="https://example.com/hook"
          data-testid="webhook-url"
        />
        {urlProblem && (
          <span className="rule-editor-drawer__field-error" data-testid="webhook-url-error">
            {urlProblem}
          </span>
        )}
      </div>

      <div className="rule-editor-drawer__field">
        <label className="rule-editor-drawer__label">Authentication</label>
        <select
          value={authType}
          onChange={(e) => handleAuthTypeChange(e.target.value as WebhookAuthConfig['type'])}
          data-testid="webhook-auth-type"
        >
          <option value="none">None</option>
          <option value="bearer">Bearer token</option>
          <option value="basic">Basic auth</option>
          <option value="api_key">API key header</option>
        </select>
      </div>

      {authBearer && (
        <div className="rule-editor-drawer__field">
          <label className="rule-editor-drawer__label">Token</label>
          <input
            value={authBearer.token}
            onChange={(e) => updateAuth({ type: 'bearer', token: e.target.value })}
            data-testid="webhook-auth-token"
          />
        </div>
      )}

      {authBasic && (
        <>
          <div className="rule-editor-drawer__field">
            <label className="rule-editor-drawer__label">Username</label>
            <input
              value={authBasic.username}
              onChange={(e) => updateAuth({ type: 'basic', username: e.target.value })}
              data-testid="webhook-auth-username"
            />
          </div>
          <div className="rule-editor-drawer__field">
            <label className="rule-editor-drawer__label">Password</label>
            <input
              type="password"
              value={authBasic.password}
              onChange={(e) => updateAuth({ type: 'basic', password: e.target.value })}
              data-testid="webhook-auth-password"
            />
          </div>
        </>
      )}

      {authApiKey && (
        <>
          <div className="rule-editor-drawer__field">
            <label className="rule-editor-drawer__label">Header name</label>
            <input
              value={authApiKey.headerName}
              onChange={(e) => updateAuth({ type: 'api_key', headerName: e.target.value })}
              data-testid="webhook-auth-header-name"
            />
          </div>
          <div className="rule-editor-drawer__field">
            <label className="rule-editor-drawer__label">Key</label>
            <input
              value={authApiKey.key}
              onChange={(e) => updateAuth({ type: 'api_key', key: e.target.value })}
              data-testid="webhook-auth-key"
            />
          </div>
        </>
      )}

      <div className="rule-editor-drawer__field">
        <label className="rule-editor-drawer__label">Timeout (ms)</label>
        <input
          type="number"
          min={1000}
          max={30000}
          value={value.timeoutMs ?? 10000}
          onChange={(e) => onChange({ ...value, timeoutMs: Number(e.target.value) })}
          data-testid="webhook-timeout"
        />
      </div>
    </>
  );
}
